import { deriveCustomBreakdowns } from 'features/charts-v2/derive-custom-breakdowns'
import {
  METRICS,
  type MetricType,
  type NumericOperatorType,
  type SelectedFilterOption,
  type TimeBucketOption,
} from 'features/charts-v2/types'
import type { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import type { Breakdown } from 'features/charts-v2/types/filter-hash/v2'
import type { PanelQueryFields } from 'features/charts-v2/types/stored-panel'
import type {
  Breakdown as YogiQLBreakdown,
  ChartRequest,
  Condition,
  FieldCondition,
  Scalar,
} from 'features/charts-v2/types/yogiql'
import {
  YOGIQL_CHART_SPECS,
  type YogiQLChartName,
} from 'features/charts-v2/types/yogiql-chart-specs'
import {
  breakdownKey,
  CHART_BY_TYPE,
  YOGIQL_METRIC,
  GROUP_FIELDS,
  metricKey,
  NUMERIC_METRIC,
  NOT_PROMOTIONAL,
  NUMERIC_OP,
  panelBreakdownOrder,
  PROMOTION_PREFIX,
  RIDGELINE_RATING,
  SEARCH_OP,
  STANDARD_BREAKDOWN,
  STANDARD_FILTER_FIELD,
} from 'features/charts-v2/utils/to-yogiql-chart-request'
import type { FilterOption, FilterOptionValue } from 'features/filters/types'
import { ALIAS_FOR_NULL_VALUES } from 'src/constants/filter-values'

/** The panel's query fields, or why the request cannot be one. `dropped`
 *  names theme / dataset values no longer in the hub's options. */
export type YogiQLDecoding =
  | { ok: true; fields: PanelQueryFields; dropped: string[] }
  | { ok: false; reason: string }

/** The filter half of a panel: what a filter-only request stands in for. */
export type FilterQueryFields = Pick<
  PanelQueryFields,
  | 'filters'
  | 'dateRange'
  | 'searchTerms'
  | 'searchCriteria'
  | 'smartSearchEnabled'
>

/** The filter fields, or why the conditions cannot be read as them. */
export type YogiQLFiltersDecoding =
  | { ok: true; fields: FilterQueryFields; dropped: string[] }
  | { ok: false; reason: string }

const TIME_BUCKETS = new Set(['day', 'week', 'month', 'quarter', 'year'])
const GROUPED_ID: Record<string, 'brand' | 'product' | 'theme'> = {
  parent_theme: 'theme',
  brand_group: 'brand',
  product_group: 'product',
}
const GROUPABLE = new Set(['brand', 'product', 'theme'])

const invert = <V extends string>(
  table: Partial<Record<V, string>>,
): Record<string, V[]> => {
  const inverse: Record<string, V[]> = {}
  for (const [key, value] of Object.entries(table) as [V, string][]) {
    ;(inverse[value] ??= []).push(key)
  }
  return inverse
}
const CHART_TYPE = invert(CHART_BY_TYPE)
const BREAKDOWN_ID = invert(
  Object.fromEntries(
    Object.entries(STANDARD_BREAKDOWN).map(([id, b]) => [id, breakdownKey(b)]),
  ),
)
const FILTER_FIELD = invert(STANDARD_FILTER_FIELD)
const NUMERIC_FIELD = invert(
  Object.fromEntries(
    Object.entries(NUMERIC_METRIC).map(([f, m]) => [f, metricKey(m)]),
  ),
)
// NPS is unsupported (06 §12): avg(rating) always reads back as Rating.
const METRIC_BY_KEY: Record<string, MetricType> = Object.fromEntries(
  (
    Object.entries(YOGIQL_METRIC) as [
      MetricType,
      (typeof YOGIQL_METRIC)[MetricType],
    ][]
  )
    .filter(([id]) => id !== METRICS.NET_PROMOTER_SCORE.id)
    .map(([id, m]) => [metricKey(m), id]),
)
const BOX_BY_OP = Object.fromEntries(
  Object.entries(SEARCH_OP).map(([box, op]) => [op, box]),
)
const NUMERIC_OPERATOR = Object.fromEntries(
  (Object.entries(NUMERIC_OP) as [NumericOperatorType, string][])
    .filter(([operator]) => operator !== 'not equal to')
    .map(([operator, op]) => [op, operator]),
) as Record<string, NumericOperatorType>

const fromInstant = (iso: Scalar | undefined): number =>
  typeof iso === 'string' ? Math.round(Date.parse(iso) / 1000) : 0

/**
 * Read a stored YogiQL request back into the panel's query fields: the
 * inverse of `toYogiQLChartRequest` in its `store` purpose. Values are read
 * against the hub's filter options, so the builder shows the spelling its own
 * picker offers, and theme / dataset names resolve to every uuid they carry
 * today (06 §30). Pure.
 */
export function fromYogiQLChartRequest(
  request: ChartRequest,
  filterOptions: FilterOption[],
): YogiQLDecoding {
  const fail = (reason: string): YogiQLDecoding => ({ ok: false, reason })
  const chartType = CHART_TYPE[request.chart_type]?.[0] as
    | ChartTypeV2
    | undefined
  // Every panel chart type maps to a registry chart (`CHART_BY_TYPE`).
  const spec = chartType
    ? YOGIQL_CHART_SPECS[request.chart_type as YogiQLChartName]
    : undefined
  if (!chartType || !spec) {
    return fail(`chart type '${request.chart_type}' has no panel chart`)
  }
  const options = new FilterOptions(filterOptions)

  const placed = request.breakdowns ?? []
  const encoded = panelBreakdownOrder(spec, placed.length).map(
    (position) => placed[position]!,
  )
  let timeBucket: TimeBucketOption | null = null
  if (spec.name === 'line') {
    const time = encoded.pop()
    if (time?.field !== 'date' || !TIME_BUCKETS.has(time.fn ?? '')) {
      return fail('a line request has no time breakdown last')
    }
    timeBucket = time.fn as TimeBucketOption
  }
  if (spec.name === 'ridgeline') encoded.pop()
  const breakdowns = decodeBreakdowns(encoded, spec.name, options)
  if (typeof breakdowns === 'string') return fail(breakdowns)

  const metrics = decodeMetrics(request, spec.name)
  if (typeof metrics === 'string') return fail(metrics)

  const ridgelineGuard = JSON.stringify(RIDGELINE_RATING.filters[0])
  const conditions = (request.filters ?? []).filter(
    (condition) =>
      spec.name !== 'ridgeline' || JSON.stringify(condition) !== ridgelineGuard,
  )
  const filters = fromYogiQLFilters(conditions, filterOptions)
  if (!filters.ok) return filters
  const { filters: panelFilters, dateRange, ...search } = filters.fields
  return {
    ok: true,
    fields: {
      chartType,
      metrics,
      breakdowns,
      filters: panelFilters,
      dateRange,
      timeBucket,
      ...search,
    },
    dropped: filters.dropped,
  }
}

/**
 * Read YogiQL conditions back into a panel's filter fields: the inverse of
 * `toYogiQLFilters`. Values are read against the hub's filter options, as
 * `fromYogiQLChartRequest` reads them. Pure.
 */
export function fromYogiQLFilters(
  conditions: Condition[],
  filterOptions: FilterOption[],
): YogiQLFiltersDecoding {
  const options = new FilterOptions(filterOptions)
  const fields: FilterQueryFields = {
    filters: [],
    dateRange: { startDate: 0, endDate: 0 },
    searchTerms: [],
    searchCriteria: {},
    smartSearchEnabled: false,
  }
  const dropped: string[] = []
  // `brand_group` reads back as the panel's brand filter, which holds one list.
  const named = conditions.flatMap(leafFields)
  if (named.includes(BRAND_GROUP) && named.includes('brand')) {
    return {
      ok: false,
      reason: `'${BRAND_GROUP}' and 'brand' are one panel filter`,
    }
  }
  // A parent theme reads as its themes only where that cannot merge two lists
  // into the panel's one theme filter; otherwise it stays a parent theme.
  const asThemes =
    named.filter((f) => f === PARENT_THEME).length === 1 &&
    !named.includes('theme')
  for (const condition of conditions) {
    const error = decodeCondition(condition, fields, options, dropped, asThemes)
    if (error) return { ok: false, reason: error }
  }
  fields.filters = fields.filters.filter(
    (f) => f.values.length || f.numericValue !== undefined || isRanged(f),
  )
  return { ok: true, fields, dropped }
}

const isRanged = (f: SelectedFilterOption): boolean =>
  f.min !== undefined || f.max !== undefined || f.valueType !== undefined

function decodeMetrics(
  request: ChartRequest,
  chart: string,
): MetricType[] | string {
  const plain = request.metrics.map((m) => ('field' in m ? m : null))
  if (plain.some((m) => m === null))
    return 'a stored request has a derived metric'
  const keys = plain.map((m) => metricKey(m!))
  // The score pick on a ridgeline is the average's field; on a sankey, the count.
  const picked =
    chart === 'ridgeline'
      ? keys.filter((k) => k.startsWith('avg('))
      : chart === 'sankey'
        ? keys.slice(0, 1)
        : keys
  const metrics: MetricType[] = []
  for (const key of picked) {
    const metric = METRIC_BY_KEY[key]
    if (!metric) return `metric ${key} has no panel metric`
    metrics.push(metric)
  }
  return metrics
}

function decodeBreakdowns(
  encoded: YogiQLBreakdown[],
  chart: string,
  options: FilterOptions,
): Breakdown[] | string {
  const breakdowns: Breakdown[] = []
  for (let i = 0; i < encoded.length; i++) {
    const yogiql = encoded[i]!
    const groupedId = GROUPED_ID[yogiql.field]
    if (groupedId && !yogiql.fn) {
      breakdowns.push({ kind: 'standard', id: groupedId, grouped: true })
      // A sankey draws a grouped breakdown as group then member (one panel breakdown).
      const member = STANDARD_BREAKDOWN[groupedId]!
      const next = encoded[i + 1]
      if (
        chart === 'sankey' &&
        GROUP_FIELDS.has(yogiql.field) &&
        next &&
        breakdownKey(next) === breakdownKey(member)
      ) {
        i++
      }
      continue
    }
    const id = BREAKDOWN_ID[breakdownKey(yogiql)]?.[0]
    if (id) {
      breakdowns.push(
        GROUPABLE.has(id)
          ? {
              kind: 'standard',
              id: id as 'brand' | 'product' | 'theme',
              grouped: false,
            }
          : ({ kind: 'standard', id } as Breakdown),
      )
      continue
    }
    const custom = yogiql.fn ? undefined : options.customBreakdown(yogiql.field)
    if (!custom)
      return `breakdown '${breakdownKey(yogiql)}' has no panel breakdown`
    breakdowns.push({ kind: 'custom', ...custom })
  }
  return breakdowns
}

/** The hub's filter options, read for the spellings the page's picker uses. */
class FilterOptions {
  private readonly fields: Set<string>

  constructor(private readonly options: FilterOption[]) {
    this.fields = new Set(options.map((o) => o.field))
  }

  /** The first candidate the hub offers, else the first candidate. */
  field(candidates: string[]): string {
    return candidates.find((c) => this.fields.has(c)) ?? candidates[0]!
  }

  values(field: string): FilterOptionValue[] {
    const option = this.options.find((o) => o.field === field)
    return option && 'values' in option ? option.values : []
  }

  customerBrands(field: string): string[] {
    const option = this.options.find((o) => o.field === field)
    return option && 'customer_brands' in option
      ? (option.customer_brands ?? [])
      : []
  }

  customBreakdown(
    field: string,
  ): Omit<Extract<Breakdown, { kind: 'custom' }>, 'kind'> | undefined {
    const custom = deriveCustomBreakdowns(this.options).find(
      (c) => c.field === field,
    )
    return (
      custom && {
        field: custom.field,
        alias: custom.alias,
        model: custom.model,
      }
    )
  }
}

// --- Conditions ---

type FilterDraft = Omit<SelectedFilterOption, 'values'> & { values: string[] }

function addFilter(fields: FilterQueryFields, next: FilterDraft): void {
  const existing = fields.filters.find((f) => f.field === next.field)
  if (!existing) {
    fields.filters.push(next)
    return
  }
  // A panel filter that emits several conditions (a score range beside its
  // buckets, rating-only beside the stars) reads back as one filter.
  existing.values = [...existing.values, ...next.values]
  if (next.operator !== 'is') existing.operator = next.operator
  for (const key of ['min', 'max', 'valueType', 'numericValue'] as const) {
    if (next[key] !== undefined) existing[key] = next[key]
  }
}

const withNull = (values: string[], hasNull: boolean | undefined): string[] =>
  hasNull ? [...values, ALIAS_FOR_NULL_VALUES] : values

const strings = (values: Scalar[] | undefined): string[] =>
  (values ?? []).map(String)

/** A field condition's membership reading: `is` / `is not` plus its values. */
function membership(
  c: FieldCondition,
  forceNull: boolean,
): { operator: 'is' | 'is not'; values: string[] } | null {
  switch (c.op) {
    case 'in':
      return {
        operator: 'is',
        values: withNull(strings(c.values), c.include_null || forceNull),
      }
    case 'not_in':
      return {
        operator: 'is not',
        values: withNull(strings(c.values), forceNull),
      }
    case 'is_null':
      return { operator: 'is', values: [ALIAS_FOR_NULL_VALUES] }
    case 'is_not_null':
      return { operator: 'is not', values: [ALIAS_FOR_NULL_VALUES] }
    default:
      return null
  }
}

function decodeCondition(
  c: Condition,
  fields: FilterQueryFields,
  options: FilterOptions,
  dropped: string[],
  asThemes: boolean,
): string | null {
  const unreadable = `condition ${JSON.stringify(c)} has no panel filter`
  if ('metric' in c) return numeric(c, false, fields) ? null : unreadable
  if ('not' in c) {
    const inner = c.not
    if ('metric' in inner)
      return numeric(inner, true, fields) ? null : unreadable
    return booleanFilter(inner, 'is not', fields, options) ? null : unreadable
  }
  if ('and' in c) {
    // `is not` with a null alias: not_in beside is_not_null.
    const [notIn, notNull] = c.and as FieldCondition[]
    if (
      c.and.length !== 2 ||
      notIn?.op !== 'not_in' ||
      notNull?.op !== 'is_not_null' ||
      notIn.field !== notNull.field
    ) {
      return unreadable
    }
    return fieldCondition(notIn, fields, options, dropped, true, asThemes)
  }
  if ('or' in c) {
    const [first, second] = c.or as FieldCondition[]
    if (
      first?.field === 'rating' &&
      first.op === 'is_null' &&
      second?.field === 'rating_only'
    ) {
      addFilter(fields, {
        field: options.field(['score', 'post_score']),
        operator: 'is',
        values: [ALIAS_FOR_NULL_VALUES],
        valueType: second.value === false ? 1 : 2,
      })
      return null
    }
    return booleanFilter(c, 'is', fields, options) ? null : unreadable
  }
  return fieldCondition(c, fields, options, dropped, false, asThemes)
}

function numeric(
  c: Extract<Condition, { metric: unknown }>,
  negated: boolean,
  fields: FilterQueryFields,
): boolean {
  const field = NUMERIC_FIELD[metricKey(c.metric)]?.[0]
  const operator: NumericOperatorType | undefined = negated
    ? c.op === 'eq'
      ? 'not equal to'
      : undefined
    : NUMERIC_OPERATOR[c.op]
  if (!field || !operator) return false
  fields.filters.push({ field, operator, values: [], numericValue: c.value })
  return true
}

function fieldCondition(
  c: FieldCondition,
  fields: FilterQueryFields,
  options: FilterOptions,
  dropped: string[],
  forceNull: boolean,
  asThemes: boolean,
): string | null {
  switch (c.field) {
    case 'date':
      if (c.op === 'between') {
        fields.dateRange = {
          startDate: fromInstant(c.values?.[0]),
          endDate: fromInstant(c.values?.[1]),
        }
      } else if (c.op === 'gte')
        fields.dateRange.startDate = fromInstant(c.value)
      else if (c.op === 'lte') fields.dateRange.endDate = fromInstant(c.value)
      else return `date condition '${c.op}' has no panel date range`
      return null
    case 'text_search':
      if (c.op === 'smart_search') {
        fields.searchTerms = strings(c.values).slice(0, 1)
        fields.smartSearchEnabled = true
        return null
      }
      if (!BOX_BY_OP[c.op]) return `search '${c.op}' has no keyword box`
      fields.searchCriteria[BOX_BY_OP[c.op]!] = strings(c.values)
      return null
    case 'rating_only':
      addFilter(fields, {
        field: options.field(['score', 'post_score']),
        operator: 'is',
        values: [],
        valueType: c.value === false ? 1 : 2,
      })
      return null
    case 'rating':
    case 'sentiment':
    case 'post_sentiment':
      return scoreCondition(c, fields, options, forceNull)
    default:
  }
  if (c.op === 'eq' && typeof c.value === 'boolean') {
    return booleanFilter(c, 'is', fields, options)
      ? null
      : `condition on '${c.field}' has no panel filter`
  }
  return fieldMembership(c, fields, options, dropped, forceNull, asThemes)
}

// A score field reads its buckets (`fn`) as values and a plain range as min/max.
function scoreCondition(
  c: FieldCondition,
  fields: FilterQueryFields,
  options: FilterOptions,
  forceNull: boolean,
): string | null {
  const field =
    c.field === 'rating' ? options.field(['score', 'post_score']) : c.field
  const range = !c.fn && ['between', 'gte', 'lte'].includes(c.op)
  if (range) {
    const [low, high] =
      c.op === 'between' ? (c.values ?? []) : [c.value, c.value]
    addFilter(fields, {
      field,
      operator: 'is',
      values: c.include_null ? [ALIAS_FOR_NULL_VALUES] : [],
      ...(c.op !== 'lte' ? { min: Number(low) } : {}),
      ...(c.op !== 'gte' ? { max: Number(high) } : {}),
    })
    return null
  }
  const read = membership(c, forceNull)
  if (!read)
    return `score condition '${c.op}' on '${c.field}' has no panel filter`
  // Polarity reads back in the option's own case (`positive` → `Positive`).
  const offered = options.values(field).map((v) => v.value)
  const values = read.values.map(
    (v) => offered.find((o) => o.toLowerCase() === v.toLowerCase()) ?? v,
  )
  addFilter(fields, { field, operator: read.operator, values })
  return null
}

function fieldMembership(
  c: FieldCondition,
  fields: FilterQueryFields,
  options: FilterOptions,
  dropped: string[],
  forceNull: boolean,
  asThemes: boolean,
): string | null {
  const read = membership(c, forceNull)
  if (!read || c.fn) {
    return `condition '${c.op}' on '${c.field}' has no panel filter`
  }
  const values = read.values
  if (c.field === BRAND_GROUP) {
    return brandGroupFilter(read, fields, options)
  }
  // With include_null a parent keeps the unthemed records (an empty parent),
  // which a theme filter cannot say, so it stays as written.
  if (c.field === PARENT_THEME && asThemes && !c.include_null) {
    const themes = parentThemeNames(read.values, options.values('theme'))
    if (themes) {
      addFilter(fields, {
        field: 'theme',
        operator: read.operator,
        values: resolveNames(themes, options.values('theme'), dropped),
      })
      return null
    }
  }
  if (c.field === 'theme' || c.field === 'dataset') {
    const field =
      c.field === 'theme' ? 'theme' : options.field(['proj_uuid', 'dataset'])
    const ids = resolveNames(values, options.values(field), dropped)
    // A deleted theme / dataset keeps its uuid, so the filter does not broaden.
    for (const id of c.missing ?? []) if (!ids.includes(id)) ids.push(id)
    addFilter(fields, { field, operator: read.operator, values: ids })
    return null
  }
  // The panel's `product_group` filter is Item Group, not this field.
  if (c.field === 'product_group') {
    return `condition on 'product_group' has no panel filter`
  }
  if (c.field === 'promotion') {
    // TODO(PROD-6108): match the bare type only once no cache serves the prefix.
    const offered = options.values('promotion').map((v) => v.value)
    // A stored `No Value` is a literal that matches nothing, never the null marker.
    const named = strings(c.values).map(
      (v) =>
        offered.find((o) => o === v || o === `${PROMOTION_PREFIX}${v}`) ?? v,
    )
    // 06 §29: an `is` names the null rows when it admits them; the panel's
    // `is not` keeps them unless it names them.
    const namesNull =
      c.op === 'is_null' ||
      c.op === 'is_not_null' ||
      (c.op === 'in' && (c.include_null || forceNull)) ||
      (c.op === 'not_in' && !c.include_null)
    if (namesNull) named.push(NOT_PROMOTIONAL)
    addFilter(fields, {
      field: 'promotion',
      operator: read.operator,
      values: named,
    })
    return null
  }
  const field = options.field(FILTER_FIELD[c.field] ?? [c.field])
  addFilter(fields, { field, operator: read.operator, values })
  return null
}

const BRAND_GROUP = 'brand_group'
const MY_BRANDS = 'My Brands'
const COMPETITOR_BRANDS = 'Competitor Brands'

// A panel holds brands, never a brand group: the legacy route drops a filter
// field it has no column for, so a `brand_group` panel filter silently runs
// unfiltered. Read the group as the brand picker selects one: its member
// brands, under `is` — My Brands the offered brands among the customer's,
// Competitor Brands every other offered brand.
function brandGroupFilter(
  read: { operator: 'is' | 'is not'; values: string[] },
  fields: FilterQueryFields,
  options: FilterOptions,
): string | null {
  const field = options.field(FILTER_FIELD['brand'] ?? ['brand'])
  const owned = options.customerBrands(field)
  if (!owned.length) {
    return `filter field '${BRAND_GROUP}' needs owned brands, which this hub has none of`
  }
  const unknown = read.values.find(
    (v) => v !== MY_BRANDS && v !== COMPETITOR_BRANDS,
  )
  if (unknown !== undefined) {
    return `'${unknown}' is not a value of '${BRAND_GROUP}'`
  }
  const named = new Set(read.values)
  const selected = [MY_BRANDS, COMPETITOR_BRANDS].filter(
    (group) => named.has(group) === (read.operator === 'is'),
  )
  if (selected.length === 0) {
    return `condition on '${BRAND_GROUP}' selects no brands`
  }
  // Both groups are every brand: no filter at all.
  if (selected.length === 2) return null
  const mine = new Set(owned)
  const members = options
    .values(field)
    .map((v) => v.value)
    .filter((brand) => mine.has(brand) === (selected[0] === MY_BRANDS))
  if (members.length === 0) {
    return `'${selected[0]}' holds no brands on this hub`
  }
  addFilter(fields, { field, operator: 'is', values: members })
  return null
}

const PARENT_THEME = 'parent_theme'

// The theme picker writes a parent as every `Parent: Child` theme under it
// (the parent is the text before the first colon, untrimmed, as the stored
// `parent_theme` is), so a panel reads one that way. Null when that would
// change what matches: a null names records the parent field never holds, and
// a parent the hub has no themes under would leave an empty theme filter,
// which matches everything; the parent theme is then kept as written.
function parentThemeNames(
  parents: string[],
  offered: FilterOptionValue[],
): string[] | null {
  if (parents.length === 0 || parents.includes(ALIAS_FOR_NULL_VALUES)) {
    return null
  }
  const byParent = new Map<string, Set<string>>()
  for (const { value } of offered) {
    const parent = value.split(':', 1)[0] ?? value
    byParent.set(parent, (byParent.get(parent) ?? new Set()).add(value))
  }
  const names = new Set<string>()
  for (const parent of parents) {
    const children = byParent.get(parent)
    if (!children) return null
    for (const child of children) names.add(child)
  }
  return [...names]
}

function leafFields(c: Condition): string[] {
  if ('metric' in c) return []
  if ('not' in c) return leafFields(c.not)
  if ('and' in c) return (c.and as Condition[]).flatMap(leafFields)
  if ('or' in c) return (c.or as Condition[]).flatMap(leafFields)
  return [c.field]
}

// A name stands for every uuid the hub gives it today; a name the hub no
// longer has drops out (the deferred resolution, PROD-5925).
function resolveNames(
  names: string[],
  offered: FilterOptionValue[],
  dropped: string[],
): string[] {
  const ids: string[] = []
  for (const name of names) {
    if (name === ALIAS_FOR_NULL_VALUES) {
      ids.push(name)
      continue
    }
    const matches = offered.filter((v) => v.value === name)
    if (!matches.length) dropped.push(name)
    for (const v of matches) {
      for (const id of [
        v.proj_uuid,
        v.cluster_uuid,
        ...(v.identifiers ?? []),
      ]) {
        if (id && !ids.includes(id)) ids.push(id)
      }
    }
  }
  return ids
}

// The legacy `Verified` / `Not Verified` convention on a boolean custom field.
function booleanFilter(
  c: Condition,
  operator: 'is' | 'is not',
  fields: FilterQueryFields,
  options: FilterOptions,
): boolean {
  const leaves: FieldCondition[] =
    'or' in c ? (c.or as FieldCondition[]) : [c as FieldCondition]
  const field = leaves[0]?.field
  if (!field) return false
  const offered = options.values(field).map((v) => v.value)
  const spell = (value: boolean): string => {
    const candidates = value
      ? [field, 'true', 'True']
      : [`Not ${field}`, 'false', 'False']
    return candidates.find((v) => offered.includes(v)) ?? candidates[0]!
  }
  const values: string[] = []
  for (const leaf of leaves) {
    if (leaf.field !== field) return false
    if (leaf.op === 'is_null') values.push(ALIAS_FOR_NULL_VALUES)
    else if (leaf.op === 'eq' && typeof leaf.value === 'boolean') {
      values.push(spell(leaf.value))
      if (leaf.include_null) values.push(ALIAS_FOR_NULL_VALUES)
    } else return false
  }
  addFilter(fields, { field, operator, values })
  return true
}
